import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Layout/Navbar';
import { Sidebar } from './components/Layout/Sidebar';
import { RightPanel } from './components/Layout/RightPanel';
import { PostCard } from './components/Post/PostCard';
import { PostComposer } from './components/Post/PostComposer';
import { Lightbox } from './components/Post/Lightbox';
import { ProfileHeader } from './components/Profile/ProfileHeader';
import { AuthModal } from './components/Auth/AuthModal';
import { TwoFactorModal } from './components/Auth/TwoFactorModal';
import { ChatDrawer } from './components/Chat/ChatDrawer';
import { CommunityView } from './components/Communities/CommunityView';
import { NotificationList } from './components/Notifications/NotificationList';
import { SettingsModal } from './components/Settings/SettingsModal';
import { InstallPrompt } from './components/PWA/InstallPrompt';

import { User, Post, TabType } from './types';
import { api, setSessionToken } from './services/api';

export const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [posts, setPosts] = useState<Post[]>([]);
  const [trendingTags, setTrendingTags] = useState<{ tag: string; count: number }[]>([]);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  // Modals state
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [is2FAOpen, setIs2FAOpen] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState<string | null>(null);

  // Profile view state
  const [profileTab, setProfileTab] = useState<'posts' | 'replies' | 'media' | 'likes'>('posts');

  useEffect(() => {
    checkAuthSession();
    fetchFeed();
    // Register PWA service worker if supported
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [activeTab, searchQuery]);

  const checkAuthSession = async () => {
    try {
      const res = await api.getMe();
      if (res.authenticated && res.user) {
        setUser(res.user);
        if (res.user.custom_theme) {
          document.documentElement.setAttribute('data-theme', res.user.custom_theme);
        }
      }
    } catch {
      // Offline fallback check
      const stored = localStorage.getItem('cordle_user');
      if (stored) setUser(JSON.parse(stored));
    }
  };

  const fetchFeed = async () => {
    try {
      const res = await api.getFeed(activeTab, searchQuery);
      setPosts(res.posts);
      if (res.trendingTags) setTrendingTags(res.trendingTags);
    } catch {
      // State handled by API fallback layer
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {}
    setSessionToken(null);
    localStorage.removeItem('cordle_user');
    setUser(null);
    setActiveTab('home');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Navbar
        user={user}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onOpenAuth={() => setIsAuthOpen(true)}
        onOpenSettings={() => setActiveTab('settings')}
        unreadNotifsCount={unreadNotifs}
      />

      {/* Main Layout Grid - Fullscreen Desktop 3-Column Width */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', width: '100%', maxWidth: '1360px', margin: '0 auto' }}>
        {/* Left Sidebar */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onOpenCompose={() => {
            if (!user) setIsAuthOpen(true);
            else setIsComposeOpen(true);
          }}
          unreadNotifsCount={unreadNotifs}
        />

        {/* Center Main Feed Content */}
        <main style={{ flex: 1, minWidth: '600px', maxWidth: '740px', padding: '0', minHeight: '100vh', borderRight: '1px solid var(--border-subtle)' }}>
          {/* Create Post Box */}
          {user && (activeTab === 'home' || isComposeOpen) && (
            <PostComposer
              user={user}
              onPostCreated={(newPost) => {
                setPosts((prev) => [newPost, ...prev]);
                setIsComposeOpen(false);
              }}
            />
          )}

          {/* Profile View Tab */}
          {activeTab === 'profile' && user && (
            <div>
              <ProfileHeader
                user={user}
                currentUser={user}
                activeTab={profileTab}
                setActiveTab={setProfileTab}
                onOpenSettings={() => setActiveTab('settings')}
                onProfileUpdated={(updated) => setUser(updated)}
              />
            </div>
          )}

          {/* Communities Tab */}
          {activeTab === 'communities' && <CommunityView currentUser={user} />}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && <NotificationList currentUser={user} />}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <SettingsModal
              user={user}
              onUpdateUser={(updated) => setUser(updated)}
              onLogout={handleLogout}
            />
          )}

          {/* Feed Posts */}
          {['home', 'following', 'trending', 'media', 'bookmarks', 'profile'].includes(activeTab) && (
            <div>
              {posts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-muted)' }}>
                  No posts found in this feed. Be the first to post on Cordle!
                </div>
              ) : (
                posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    currentUser={user}
                    onMediaClick={(url) => setLightboxMedia(url)}
                  />
                ))
              )}
            </div>
          )}
        </main>

        {/* Right Sidebar */}
        <RightPanel
          trendingTags={trendingTags}
          onSearchTag={(tag) => {
            setSearchQuery(tag);
            setActiveTab('home');
          }}
        />
      </div>

      {/* Floating Chat Button for logged-in users */}
      {user && (
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 80,
            padding: '14px 20px',
            borderRadius: '30px',
            background: 'var(--accent-gradient)',
            color: '#ffffff',
            fontWeight: '700',
            fontSize: '0.92rem',
            boxShadow: '0 8px 24px var(--accent-glow)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          💬 Live Chat
        </button>
      )}

      {/* Chat Drawer */}
      <ChatDrawer currentUser={user} isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Auth Modals */}
      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onSuccess={(u) => {
          setUser(u);
          fetchFeed();
        }}
        onRequire2FA={(uId) => {
          setTwoFAUserId(uId);
          setIs2FAOpen(true);
        }}
      />

      <TwoFactorModal
        isOpen={is2FAOpen}
        userId={twoFAUserId}
        onClose={() => setIs2FAOpen(false)}
        onSuccess={(u) => {
          setUser(u);
          fetchFeed();
        }}
      />

      {/* Media Lightbox */}
      <Lightbox mediaUrl={lightboxMedia} onClose={() => setLightboxMedia(null)} />

      {/* PWA Install Banner */}
      <InstallPrompt />
    </div>
  );
};
